import type { ChangedItems } from '@/definitions/effectiveChangeTable';

import { Fragment, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Loading from 'react-loading';

import Button from '@/components/Button';
import Header from '@/components/Header';
import MessageBox from '@/components/MessageBox';
import Table from '@/components/Table';
import { Date } from '@/components/FormElements';

import { changedItemsColumns } from '@/definitions/effectiveChangeTable';
import { formIds } from '@/definitions/formIds';
import { compareDates, getDateFromSquidex, unserializeDate } from '@/services/dates';

// TODO: Add this to Squidex
// Messages need to be specific to forms
// - One for Commercial Crime (no deductibles lower than $500)
// - One for Commercial Property (no deductibles lower than $500)
// - One for Inland Marine (no deductibles lower than $1000)
const effectiveDateMessageList: {
	id: string;
	dateStr: string;
	comparison: 'before' | 'after';
	message: string;
	formGuid: string;
}[] = [
	{
		id: 'post-june-1-crime',
		dateStr: '06/01/2025 00:00:00',
		comparison: 'after',
		message: 'Commercial Crime (C-2397): As of 6/1/2025, deductibles lower than $500 are no longer available.',
		formGuid: '68a7d8ea-872d-48ee-b716-0c40a90d7d19'
	},
	{
		id: 'post-june-1-property',
		dateStr: '06/01/2025 00:00:00',
		comparison: 'after',
		message: 'Commercial Property (C-2393): As of 6/1/2025, deductibles lower than $1,000 are no longer available.',
		formGuid: '2adf1dfe-2b9f-484d-a66d-19d43837f1ed',
	},
	{
		id: 'post-june-1-inland-marine',
		dateStr: '06/01/2025 00:00:00',
		comparison: 'after',
		message: 'Commercial Inland Marine (C-2395): As of 6/1/2025, deductibles lower than $500 are no longer available.',
		formGuid: '0bb25529-baaf-4b5c-b548-93fd2c4b7944'
	}
];

type ChangeEffectiveDateProps = {
	currentCppEffectiveDate: string,
	title?: string;
	onCancel?: () => void;
	onSuccess?: ( cppEffectiveDate: Date, invalidValues: InvalidValueObject[] ) => Promise<void>;
	onCheck?: ( newEffectiveDate: Date ) => any;
	cppForms?: any[];
};

type PostChangeDifferencesProps = {
	changedItems: ChangedItems[];
	changedForms: ChangedItems[];
};

function PostChangeDifferences({
	changedItems,
	changedForms
}: PostChangeDifferencesProps ) {
	const combinedChanges = useMemo( () => {
		return [ ...changedItems, ...changedForms ];
	}, [ changedItems, changedForms ] );

	return (
		<div className="mt-4">
			{ combinedChanges.length > 0 ? (
				<>
					<p className="mb-4">Please note that changing the effective date means the following coverage options may have also changed.</p>

					<div className="mb-6">
						<Table
							variant="mini"
							data={ combinedChanges }
							columns={ changedItemsColumns }
						/>
					</div>
				</>
			) : (
				<p className="mb-4">Changing the effective date will not result in any coverage changes.</p>
			)}
		</div>
	);
}

export default function ChangeEffectiveDate({
	currentCppEffectiveDate,
	title,
	onCancel = () => {},
	onSuccess = async () => {},
	onCheck,
	cppForms = []
}: ChangeEffectiveDateProps ) {
	const [ changedForms, setChangedForms ] = useState<ChangedItems[] | undefined>( undefined );
	const [ changedItems, setChangedItems ] = useState<ChangedItems[] | undefined>( undefined );
	const [ canSubmit, setCanSubmit ] = useState<boolean>( true );
	const [ pendingDate, setPendingDate ] = useState<Date | undefined>();
	const [ effectiveDateMessages, setEffectiveDateMessages ] = useState<string[]>( [] );
	const [ isLoading, setIsLoading ] = useState( false );
	const [ invalidValues, setInvalidValues ] = useState<InvalidValueObject[] | undefined>( undefined );
	const { handleSubmit, reset } = useFormContext();

	const clearPending = useCallback( () => {
		setChangedForms( undefined );
		setChangedItems( undefined );
		setPendingDate( undefined );
		setEffectiveDateMessages( [] );
		setInvalidValues( undefined );
		setCanSubmit( true );
	}, [ setCanSubmit, setChangedForms, setChangedItems, setEffectiveDateMessages, setPendingDate ] );

	const onClear = useCallback( () => {
		reset();
		clearPending();
		onCancel();
	}, [ onCancel, reset, clearPending ] );

	const finalize = useCallback( async ( effectiveDate?: Date, invalidValues?: InvalidValueObject[] ) => {
		setIsLoading( true );

		if ( effectiveDate ) {
			await onSuccess( effectiveDate, invalidValues || [] );
			
			setIsLoading( false );
			onClear();
		} else {
			setIsLoading( false );
		}
	}, [ onClear, onSuccess ] );

	const onSubmit = useCallback( async ( data: any ) => {
		let shouldPause = false;

		setIsLoading( true );

		// if the onCheck function is there, do the check
		if ( typeof onCheck !== 'undefined' ) {
			const checkResults = await onCheck( data.cppEffectiveDate );

			if ( checkResults.length > 0 ) {
				setInvalidValues( checkResults );

				shouldPause = true;
			}
		}

		// if there are messages to display for this effective date and application, show them
		const activeMessages = effectiveDateMessageList.filter( messageObject => {
			const isValidDate = compareDates( messageObject.dateStr, data.cppEffectiveDate, messageObject.comparison );
			const isCurrentDateValid = compareDates( messageObject.dateStr, currentCppEffectiveDate, messageObject.comparison );

			// if the current date is also in the same date range, we don't want to post messages
			return ( isValidDate && !isCurrentDateValid ) && messageObject.message;
		}).filter( messageObject => {
			return cppForms.some( form => form.applicationTypeGuid === messageObject.formGuid );
		}).map( messageObject => {
			return messageObject.message;
		}).filter( a => a );

		// check to see if we need to warn the user about cyber questionnaire
		// first check to see if the application has a cyber questionnaire:
		const hasCyberQuestionnaire = cppForms.some( form => form.applicationTypeGuid === formIds.CYBER_COVERAGE_QUESTIONNAIRE );

		// 2.) is the existing CPP going from old questionnaire to new?
		let cyberMessages: string[] = [];

		const originalCppEffectiveDate = currentCppEffectiveDate;
		const newEffectiveDate = data.cppEffectiveDate;
		const cutoverDate = getDateFromSquidex( '04/01/2026 00:00:00' );
		const isComingFromOriginal = compareDates( cutoverDate, unserializeDate( originalCppEffectiveDate ), 'before' );
		const isGoingToOriginal = compareDates( cutoverDate, unserializeDate( newEffectiveDate ), 'before' );

		// if it's going from new to old, it can't be copied, so we need to stop them here
		if ( hasCyberQuestionnaire && ( !isComingFromOriginal && isGoingToOriginal ) ) {
			cyberMessages = [ 'Due to changes in the Cyber Coverage Questionnaire (C-2271), this CPP cannot change its effective date to before 04/01/2026' ];

			setCanSubmit( false );
		}

		// if it's going from old to new, we need to warn them that the existing questionnaire
		// will be deleted and they'll have to fill it in again.
		if ( hasCyberQuestionnaire && ( isComingFromOriginal && !isGoingToOriginal ) ) {
			cyberMessages = [ 'The Cyber Coverage Questionnaire (C-2271) attached to this CPP is out of date and will be removed. It will need to be replaced with the new version of the questionnaire.' ];
		}

		setEffectiveDateMessages( [ ...activeMessages as string[], ...cyberMessages as string[] ] );

		if ( [ ...activeMessages, ...cyberMessages ].length > 0 ) {
			shouldPause = true;
		}

		// if either of the previous checks happened, pause and let the user confirm,
		// otherwise, finalize
		if ( shouldPause ) {
			setIsLoading( false );

			setPendingDate( data.cppEffectiveDate );

			return;
		}

		finalize( data.cppEffectiveDate );
	}, [ cppForms, currentCppEffectiveDate, finalize, onCheck ] );

	const shouldShowButtons = useMemo( () => {
		const hasMessages = effectiveDateMessages.length > 0;
		const hasChangedItems = ( changedItems !== undefined && changedForms !== undefined );
		const hasInvalidValues = ( invalidValues && invalidValues.length > 0 );

		return hasMessages || hasChangedItems || hasInvalidValues;
	}, [ effectiveDateMessages, changedItems, changedForms, invalidValues ] );

	return (
		<div>
			{ isLoading && (
				<div className="absolute z-10 top-0 right-0 bottom-0 left-0 bg-white flex bg-opacity-75 items-center justify-center">
					<Loading type="spinningBubbles" color="#454545" width={ 64 } height={ 64 } />
				</div>
			)}

			<Header element="h2" variant="h3">{ title || "Change CPP Effective Date" }</Header>

			{ !pendingDate && (
				<form onSubmit={ handleSubmit( onSubmit ) }>
					<Date
						label="CPP Effective Date"
						systemId="cppEffectiveDate"
					/>

					<div className="flex gap-x-4 mt-4">
						<Button type="submit" variant="primary" size="medium">Continue</Button>
						<Button type="button" variant="border" size="medium" onClick={ () => onClear() }>Cancel</Button>
					</div>
				</form>
			)}

			{ effectiveDateMessages.length > 0 && (
				<MessageBox
					size="small"
					variant="neutral"
					className="mt-4"
					rounded
				>
					<div>
						{ effectiveDateMessages.map( ( message, index ) => (
							<div key={ `message-${ index }` } className="my-2">{ message }</div>
						))}
					</div>
				</MessageBox>
			)}

			{ ( invalidValues && invalidValues.length > 0 ) && (
				<div className="mt-4">
					<p className="mb-4 text-sm">Please note that changing the effective date means the following values will be updated to reflect available deductible options.</p>

					<div className="max-h-[250px] overflow-auto">
						<table className="w-full mb-4">
							<thead>
								<tr>
									<th className="border-b border-b-rural-gray-500 p-1 text-xs text-left">Form</th>
									<th className="border-b border-b-rural-gray-500 p-1 text-xs text-left">Coverage</th>
									<th className="border-b border-b-rural-gray-500 p-1 text-xs">Current Value</th>
									<th className="border-b border-b-rural-gray-500 p-1 text-xs">New Value</th>
								</tr>
							</thead>

							<tbody>
								{ invalidValues.map( ( form: any ) => (
									<Fragment key={ form.formId }>
										{ form.questions.map( ( question: any, questionIndex: number ) => (
											<tr key={ question.systemId }>
												<td className="border-b border-b-rural-gray-300 p-1 text-xs">{ questionIndex === 0 ? form.formName : '' }</td>
												<td className="border-b border-b-rural-gray-300 p-1 text-xs">{ question.systemLabel }</td>
												<td className="border-b border-b-rural-gray-300 p-1 text-xs text-right">{ question.oldValue }</td>
												<td className="border-b border-b-rural-gray-300 p-1 text-xs text-right">{ question.newValueDisplay }</td>
											</tr>
										))}
									</Fragment>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{ ( changedItems !== undefined && changedForms !== undefined ) && (
				<PostChangeDifferences
					changedForms={ changedForms }
					changedItems={ changedItems }
				/>
			)}

			{ shouldShowButtons && (
				<div className="flex gap-x-4 mt-4">
					{ canSubmit && <Button type="button" variant="primary" size="medium" onClick={ () => finalize( pendingDate, invalidValues ) }>Submit</Button> }
					<Button type="button" variant="border" size="medium" onClick={ () => clearPending() }>Cancel</Button>
				</div>
			)}
		</div>
	);
}
